import moment from 'moment';
import { HdBodygraphChartPreset } from '../bodygraph/hd-bodygraph.models';
import {
  HdBirthLocation,
  HdBirthTimeValue,
  HdChartBirthInputs,
  HdChartDocumentResult,
  HdChartFormInitialValue,
} from './hd-chart-library.models';

/** Vorm van `preset.birthData` zoals de backend die wegschrijft (zie EgiChartDocument). */
interface StoredBirthData {
  time?: {
    utc?: string | null;
    local?: string | null;
    isInUtc?: boolean | null;
    timezone?: { id?: string | null } | null;
  } | null;
  location?: {
    city?: string | null;
    country?: string | null;
    lat?: number | null;
    lon?: number | null;
  } | null;
}

const DEFAULT_TIME: HdBirthTimeValue = { time: '12:00', isUtc: false, display: '24h' };

const nonEmpty = (value: string | null | undefined): string => (value ?? '').trim();

/**
 * Mapt een geladen chartdocument naar voorinvulwaarden voor het edit-formulier.
 *  - Tijd: oorspronkelijk-UTC-invoer → utc-veld (isUtc aan); anders lokale wandkloktijd.
 *  - Locatie: voorinvullen zodra er een stad bekend is. calculate-from-birth bewaart bij
 *    een overgeslagen lookup de volledige place-string als city ("Turnhout, Belgium") en
 *    een léég land — het stuk na de komma dient dan als landnaam-hint die de picker
 *    tegen de landenlijst resolvet. Ontbrekende lat/lon zijn geen blokker: een
 *    herberekening geocodeert dan backend-side via de place-string.
 */
export function buildChartEditFormValue(doc: HdChartDocumentResult): HdChartFormInitialValue {
  const birth = (doc.preset.birthData ?? {}) as StoredBirthData;
  const time = birth.time ?? {};
  const storedLocation = birth.location ?? {};

  let date: HdChartFormInitialValue['date'];
  let timeValue = DEFAULT_TIME;
  const utc = time.utc ? moment.utc(time.utc, moment.ISO_8601) : undefined;
  // Kloktijd letterlijk lezen, net als de geboorterij in de chart (hd-advanced-properties): een
  // zone-suffix (Maia-artefact "Z") mag de tijd niet via de browsertijdzone laten verschuiven.
  const local = time.local ? moment(time.local.replace(/(Z|[+-]\d{2}:?\d{2})$/, ''), moment.ISO_8601) : undefined;
  if (time.isInUtc && utc?.isValid()) {
    date = utc;
    timeValue = { time: utc.format('HH:mm'), isUtc: true, display: '24h' };
  } else if (local?.isValid()) {
    date = local;
    timeValue = { time: local.format('HH:mm'), isUtc: false, display: '24h' };
  } else if (utc?.isValid()) {
    date = utc;
    timeValue = { time: utc.format('HH:mm'), isUtc: true, display: '24h' };
  }

  const storedCity = nonEmpty(doc.meta.birthCity) || nonEmpty(storedLocation.city);
  const countryCode = nonEmpty(doc.meta.birthCountry) || nonEmpty(storedLocation.country);
  const latitude = doc.meta.latitude ?? storedLocation.lat ?? undefined;
  const longitude = doc.meta.longitude ?? storedLocation.lon ?? undefined;
  const timezone = nonEmpty(doc.meta.timezone) || nonEmpty(time.timezone?.id);

  let location: HdBirthLocation | null = null;
  if (storedCity) {
    const [cityPart, ...rest] = storedCity.split(',');
    const countryNameHint = rest.join(',').trim();
    // De opgeslagen string is al geocodeerbaar ("Stad, Land"); anders de code aanplakken.
    const place = countryNameHint || !countryCode ? storedCity : `${storedCity}, ${countryCode}`;
    location = {
      place,
      city: cityPart.trim(),
      countryCode,
      countryName: countryNameHint,
      latitude,
      longitude,
      timezone,
    };
  }

  return { name: doc.meta.name ?? doc.preset.name ?? '', date, time: timeValue, location };
}

/**
 * Draagt het handwerk van de gebruiker (gepinde blokjes, maankaart-layout,
 * tab-tooltip) van het originele document over op het vers herberekende —
 * dezelfde afspraak als tab-herberekenen (spec §9): verdwenen activatie-/
 * poort-keys negeert de layout vanzelf. Alleen aanwezige velden meenemen,
 * zodat het verse document geen lege sleutels oppikt.
 */
export function carryUserHandwork(fresh: HdBodygraphChartPreset, original: HdBodygraphChartPreset): HdBodygraphChartPreset {
  const carried: Partial<HdBodygraphChartPreset> = {};
  if (original.tabTooltip != null) carried.tabTooltip = original.tabTooltip;
  if (original.gateBlockLayout != null) carried.gateBlockLayout = original.gateBlockLayout;
  if (original.moonTransitLayout != null) carried.moonTransitLayout = original.moonTransitLayout;
  if (original.justNowTransitLayout != null) carried.justNowTransitLayout = original.justNowTransitLayout;
  return { ...fresh, ...carried };
}

/**
 * True wanneer de geboorte-invoer (datum, tijd incl. UTC-vlag, locatie) niet afwijkt
 * van de voorinvulling — dan volstaat een naamwijziging zonder herberekening.
 * Cosmetische velden (weergavemodus, countryName, place-tekst) tellen niet mee.
 */
export function birthInputsUnchanged(initial: HdChartBirthInputs, current: HdChartBirthInputs): boolean {
  const sameDate =
    initial.date == null || current.date == null
      ? initial.date == null && current.date == null
      : initial.date.format('YYYY-MM-DD') === current.date.format('YYYY-MM-DD');

  const sameTime = initial.time.time === current.time.time && initial.time.isUtc === current.time.isUtc;

  const a = initial.location;
  const b = current.location;
  const sameLocation =
    a === null || b === null
      ? a === null && b === null
      : a.city === b.city &&
        // De prefill kent de landcode soms pas nadat de picker die tegen de landenlijst
        // geresolved heeft ('' → 'BE'); vergelijk codes daarom enkel wanneer beide bekend zijn.
        (!a.countryCode || !b.countryCode || a.countryCode === b.countryCode) &&
        a.latitude === b.latitude &&
        a.longitude === b.longitude &&
        a.timezone === b.timezone;

  return sameDate && sameTime && sameLocation;
}
